import type { Cam } from '@/engine/types';

/**
 * YooNee! (app.xensci.yoonee) campus CCTV provider.
 *
 * The KMITL wayfinding app exposes its traffic cameras as token-gated HLS. The
 * backend sends NO CORS headers, so the browser can't call yoonee.xensci.app
 * directly — every request here is same-origin under {@link CCTV_PROXY}, which a
 * Cloudflare Worker (prod) / Vite dev proxy forwards to the real host.
 *
 * Flow mirrors the app: availability → session token → tokenized stream URL.
 * Recovered from the decompiled base split:
 *   GET  /api/cctv                      -> CCTVCamera[]  { id, name, description?, latitude, longitude, channels:number[], direction? }
 *   GET  /api/cctv/session              -> { token, ttl } (ttl seconds; ~120)
 *   GET  /api/cctv/availability?camera= -> { open, reason? }
 *   stream: /api/cctv/hls/api/stream.m3u8?src=<id.toLowerCase()>_h264&t=<token>
 */

/**
 * Same-origin mount for the CCTV proxy (Worker in prod, Vite proxy in dev).
 *
 * Everything, media included, has to go through here. The origin gates the
 * stream twice, and the two gates rule out every other arrangement:
 *  - it answers 403 to any request carrying `Sec-Fetch-Site: cross-site`, so the
 *    browser can never fetch the stream itself (that header is forbidden to JS);
 *  - it will not open a stream session for a datacenter IP — the master playlist
 *    returns 200 but the child it references 404s, no matter the headers.
 * So the proxy must be same-origin to the page AND run on an ordinary client IP.
 * `npm run dev` satisfies both; a Cloudflare Worker satisfies neither.
 */
export const CCTV_PROXY = '/cctv';
/** Panel group / Cam.site tag for CCTV cameras. */
export const CCTV_SITE = 'CCTV';

const api = (path: string): string => `${CCTV_PROXY}/api/cctv${path}`;

interface RawCamera {
  id: string;
  name: string;
  description?: string | null;
  latitude: number;
  longitude: number;
  channels: number[];
  direction?: string | null;
}
interface RawSession { token: string; ttl: number; }
interface RawAvailability { open: boolean; reason?: string | null; }

export const isYoonee = (cam: Cam): boolean => cam.provider === 'yoonee';

/**
 * Cameras observed in the catalogue before it was trimmed (it held 9 on
 * 2026-09-28 and holds 5 now). An earlier sync deleted retired cameras outright,
 * so these were lost locally; they are re-seeded as 'absent' and simply start
 * playing again if the catalogue restores them. Only ids actually seen upstream
 * belong here — a name is set only where the catalogue gave one.
 */
export const CCTV_REMEMBERED: { id: string; name?: string }[] = [
  { id: 'MRS1', name: 'Main Road Security 01' },
  { id: 'SP7' },
];

/** A placeholder record for a camera that is not in the catalogue right now. */
export function absentCctvCam(id: string, name?: string): Cam {
  return {
    id,
    site: CCTV_SITE,
    idx: null,
    url: '',
    status: 'absent',
    lastSeen: null,
    provider: 'yoonee',
    ...(name ? { name } : {}),
  };
}

/** The origin that serves the media itself (the playlist hands the browser off
 *  to it with its own `vt` token). Needed in CSP, never fetched by the proxy —
 *  the token flow through {@link CCTV_PROXY} is what authorises it. */
export const CCTV_MEDIA_ORIGIN = 'https://cctv.apps.scira.kmitl.ac.th';

/**
 * Whether CCTV can play here. It can, as of the backend change that moved the
 * media onto {@link CCTV_MEDIA_ORIGIN}: the browser now fetches the stream
 * itself (from its own IP, cross-site allowed) instead of the proxy fetching a
 * child playlist the backend would not serve to a datacenter IP.
 * Set `localStorage['gin9:cctv'] = 'off'` to hide it again.
 */
export function cctvSupported(): boolean {
  try { return localStorage.getItem('gin9:cctv') !== 'off'; } catch { return true; }
}


// ---- session token: global (not per-camera), ttl-bound, cached in-memory ----
let tok: { value: string; exp: number } | null = null;
let inflight: Promise<string> | null = null;

/** Epoch-ms when the cached token stops being usable (0 if none). */
export const cctvTokenExpiry = (): number => tok?.exp ?? 0;
/** Drop the cached token so the next call re-handshakes (use after a 401/403). */
export const cctvInvalidate = (): void => { tok = null; };

/** A valid session token, refreshing ~15 s before expiry. Single-flighted. */
export async function cctvToken(): Promise<string> {
  if (tok && Date.now() < tok.exp - 15_000) return tok.value;
  if (inflight) return inflight;
  inflight = (async (): Promise<string> => {
    try {
      const r = await fetch(api('/session'), { cache: 'no-store', credentials: 'same-origin' });
      if (!r.ok) throw new Error(`cctv session ${r.status}`);
      const s = (await r.json()) as RawSession;
      const ttl = Number.isFinite(s.ttl) && s.ttl > 0 ? s.ttl : 120;
      tok = { value: s.token, exp: Date.now() + ttl * 1000 };
      return s.token;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/** Live HLS URL for a camera id, carrying a fresh token. Same-origin (proxied). */
export async function cctvStreamUrl(id: string): Promise<string> {
  const t = await cctvToken();
  const src = `${id.toLowerCase()}_h264`;
  return api(`/hls/api/stream.m3u8?src=${encodeURIComponent(src)}&t=${encodeURIComponent(t)}`);
}

/** Fetch the campus camera catalogue and map it to gin9 Cam records. */
export async function listCctvCameras(): Promise<Cam[]> {
  const r = await fetch(api(''), { cache: 'no-store', credentials: 'same-origin' });
  if (!r.ok) throw new Error(`cctv list ${r.status}`);
  const raw = (await r.json()) as RawCamera[];
  return raw.map((c): Cam => {
    const note = [c.description, c.direction].filter(Boolean).join(' · ');
    return {
      id: c.id,
      site: CCTV_SITE,
      idx: null,
      url: '',
      status: 'unknown',
      lastSeen: null,
      provider: 'yoonee',
      name: c.name,
      lat: c.latitude,
      lng: c.longitude,
      note: note || undefined,
    };
  });
}

/** Optional gate: whether the backend currently serves this camera (+ a reason). */
export async function cctvAvailability(id: string): Promise<RawAvailability | null> {
  try {
    const r = await fetch(api(`/availability?camera=${encodeURIComponent(id)}`), {
      cache: 'no-store',
      credentials: 'same-origin',
    });
    if (!r.ok) return null;
    return (await r.json()) as RawAvailability;
  } catch {
    return null;
  }
}
